import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Textarea,
  Input,
  FormControl,
  FormLabel,
  Heading,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  Spinner,
  Card,
  CardBody,
  CardHeader,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Checkbox,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import offerService from "../services/offerService";

const OfferAcceptancePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);

  // Form state
  const [signature, setSignature] = useState("");
  const [comments, setComments] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Modals
  const {
    isOpen: isAcceptOpen,
    onOpen: onAcceptOpen,
    onClose: onAcceptClose,
  } = useDisclosure();
  const {
    isOpen: isDeclineOpen,
    onOpen: onDeclineOpen,
    onClose: onDeclineClose,
  } = useDisclosure();

  useEffect(() => {
    fetchOfferDetails();
  }, [token]);

  const fetchOfferDetails = async () => {
    try {
      setLoading(true);
      // Use the view endpoint which doesn't require authentication
      const response = await offerService.getPublicOffer(token);

      if (response.data.success) {
        setOffer(response.data.data);
      } else {
        setError(response.data.message || "Failed to load offer");
      }
    } catch (err) {
      console.error("Error fetching offer:", err);
      setError(
        err.response?.data?.message ||
          "Invalid or expired offer link. Please contact HR for assistance."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!signature.trim()) {
      toast({
        title: "Signature Required",
        description: "Please enter your full name as signature",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!agreedToTerms) {
      toast({
        title: "Agreement Required",
        description: "Please agree to the terms and conditions",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setProcessing(true);
    try {
      const ipAddress = await fetch("https://api.ipify.org?format=json")
        .then((res) => res.json())
        .then((data) => data.ip)
        .catch(() => "unknown");

      const userAgent = navigator.userAgent;

      const response = await offerService.acceptOffer(
        token,
        signature,
        comments,
        ipAddress,
        userAgent
      );

      if (response.data.success) {
        setAccepted(true);
        onAcceptClose();
        toast({
          title: "Offer Accepted!",
          description:
            "Congratulations! Your offer has been accepted successfully.",
          status: "success",
          duration: 5000,
        });
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error("Error accepting offer:", err);
      toast({
        title: "Error",
        description:
          err.response?.data?.message ||
          "Failed to accept offer. Please try again.",
        status: "error",
        duration: 5000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!comments.trim()) {
      toast({
        title: "Reason Required",
        description: "Please provide a reason for declining",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setProcessing(true);
    try {
      const ipAddress = await fetch("https://api.ipify.org?format=json")
        .then((res) => res.json())
        .then((data) => data.ip)
        .catch(() => "unknown");

      const userAgent = navigator.userAgent;

      const response = await offerService.declineOffer(
        token,
        comments,
        ipAddress,
        userAgent
      );

      if (response.data.success) {
        setDeclined(true);
        onDeclineClose();
        toast({
          title: "Offer Declined",
          description: "Your response has been recorded.",
          status: "info",
          duration: 5000,
        });
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error("Error declining offer:", err);
      toast({
        title: "Error",
        description:
          err.response?.data?.message ||
          "Failed to decline offer. Please try again.",
        status: "error",
        duration: 5000,
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading offer details...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
        p={4}
      >
        <Card maxW="600px" w="full">
          <CardBody>
            <VStack spacing={4} align="center">
              <WarningIcon boxSize={12} color="red.500" />
              <Heading size="lg">Unable to Load Offer</Heading>
              <Text textAlign="center" color="gray.600">
                {error}
              </Text>
              <Text fontSize="sm" color="gray.500">
                If you believe this is an error, please contact the HR
                department.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  if (accepted) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
        p={4}
      >
        <Card maxW="600px" w="full">
          <CardBody>
            <VStack spacing={4} align="center">
              <CheckCircleIcon boxSize={16} color="green.500" />
              <Heading size="lg">Offer Accepted!</Heading>
              <Text textAlign="center" color="gray.600">
                Congratulations on joining our team! You will receive further
                instructions via email regarding your onboarding process.
              </Text>
              <Divider />
              <VStack align="stretch" w="full" spacing={2}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Position:</Text>
                  <Text>{offer.header?.designation || "N/A"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Start Date:</Text>
                  <Text>
                    {offer.details?.joiningDate
                      ? new Date(offer.details.joiningDate).toLocaleDateString()
                      : "TBD"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Location:</Text>
                  <Text>{offer.details?.location || "N/A"}</Text>
                </HStack>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  if (declined) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
        p={4}
      >
        <Card maxW="600px" w="full">
          <CardBody>
            <VStack spacing={4} align="center">
              <WarningIcon boxSize={12} color="orange.500" />
              <Heading size="lg">Offer Declined</Heading>
              <Text textAlign="center" color="gray.600">
                Thank you for your response. We appreciate your consideration
                and wish you the best in your career endeavors.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Box maxW="900px" mx="auto" px={4}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Card>
            <CardHeader>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Heading size="lg">Job Offer</Heading>
                  <Badge
                    colorScheme={
                      offer.header?.status === "sent" ? "blue" : "green"
                    }
                    fontSize="md"
                    px={3}
                    py={1}
                  >
                    {offer.header?.status?.replace("_", " ").toUpperCase()}
                  </Badge>
                </HStack>
                <Text color="gray.600">
                  Please review the offer details below and respond by{" "}
                  {offer.header?.validTill
                    ? new Date(offer.header.validTill).toLocaleDateString()
                    : "the specified date"}
                </Text>
              </VStack>
            </CardHeader>
          </Card>

          {/* Candidate Info */}
          <Card>
            <CardHeader>
              <Heading size="md">Candidate Information</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Name:</Text>
                  <Text>{offer.acceptance?.candidateName || "N/A"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Email:</Text>
                  <Text>{offer.acceptance?.candidateEmail || "N/A"}</Text>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Position Details */}
          <Card>
            <CardHeader>
              <Heading size="md">Position Details</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Position:</Text>
                  <Text>{offer.details?.designation || "N/A"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Department:</Text>
                  <Text>{offer.details?.department || "N/A"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Location:</Text>
                  <Text>{offer.details?.location || "N/A"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Employment Type:</Text>
                  <Text>{offer.details?.employmentType || "N/A"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Joining Date:</Text>
                  <Text>
                    {offer.details?.joiningDate
                      ? new Date(offer.details.joiningDate).toLocaleDateString()
                      : "TBD"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Reporting Manager:</Text>
                  <Text>{offer.details?.reportingManager?.name || "N/A"}</Text>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Compensation */}
          <Card>
            <CardHeader>
              <Heading size="md">Compensation Package</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontWeight="bold" fontSize="lg">
                    Annual CTC:
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.600">
                    {offer.details?.currency || "INR"}{" "}
                    {offer.details?.ctc?.toLocaleString() || "N/A"}
                  </Text>
                </HStack>
                {offer.details?.salaryBreakdown && (
                  <>
                    <Divider />
                    <Text fontWeight="bold" fontSize="sm" color="gray.600">
                      Salary Breakdown:
                    </Text>
                    {Object.entries(offer.details.salaryBreakdown).map(
                      ([key, value]) =>
                        value > 0 && (
                          <HStack key={key} justify="space-between" pl={4}>
                            <Text fontSize="sm" textTransform="capitalize">
                              {key.replace(/([A-Z])/g, " $1").trim()}:
                            </Text>
                            <Text fontSize="sm">{value?.toLocaleString()}</Text>
                          </HStack>
                        )
                    )}
                  </>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Benefits */}
          {offer.details?.benefits && offer.details.benefits.length > 0 && (
            <Card>
              <CardHeader>
                <Heading size="md">Benefits & Perks</Heading>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={2}>
                  {offer.details.benefits.map((benefit, index) => (
                    <HStack key={index}>
                      <CheckCircleIcon color="green.500" />
                      <Text>{benefit}</Text>
                    </HStack>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Terms & Conditions */}
          {offer.clauses && offer.clauses.length > 0 && (
            <Card>
              <CardHeader>
                <Heading size="md">Terms & Conditions</Heading>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  {offer.clauses.map((clause, index) => (
                    <Box key={index}>
                      <Text fontWeight="bold">{clause.title}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {clause.content}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Action Buttons */}
          {!accepted && !declined && (
            <Card>
              <CardBody>
                <VStack spacing={4}>
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    Please review all details carefully before accepting or
                    declining this offer.
                  </Alert>

                  <HStack spacing={4} w="full" justify="center">
                    <Button
                      colorScheme="green"
                      size="lg"
                      leftIcon={<CheckCircleIcon />}
                      onClick={onAcceptOpen}
                      isDisabled={processing}
                    >
                      Accept Offer
                    </Button>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      size="lg"
                      onClick={onDeclineOpen}
                      isDisabled={processing}
                    >
                      Decline Offer
                    </Button>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Box>

      {/* Accept Modal */}
      <Modal isOpen={isAcceptOpen} onClose={onAcceptClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Accept Job Offer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                You are about to accept this job offer. This action cannot be
                undone.
              </Alert>

              <FormControl isRequired>
                <FormLabel>Digital Signature (Full Name)</FormLabel>
                <Input
                  placeholder="Enter your full legal name"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Comments (Optional)</FormLabel>
                <Textarea
                  placeholder="Any comments or questions..."
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                />
              </FormControl>

              <Checkbox
                isChecked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              >
                <Text fontSize="sm">
                  I have read and agree to all the terms and conditions stated
                  in this offer letter.
                </Text>
              </Checkbox>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAcceptClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleAccept}
              isLoading={processing}
              isDisabled={!signature || !agreedToTerms}
            >
              Confirm Acceptance
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Decline Modal */}
      <Modal isOpen={isDeclineOpen} onClose={onDeclineClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Decline Job Offer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                We're sorry to see you decline this offer. Please let us know
                your reason.
              </Alert>

              <FormControl isRequired>
                <FormLabel>Reason for Declining</FormLabel>
                <Textarea
                  placeholder="Please provide your reason for declining this offer..."
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={4}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeclineClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDecline}
              isLoading={processing}
              isDisabled={!comments}
            >
              Confirm Decline
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OfferAcceptancePage;
